import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { renderCell, boldCellSx } from '../../../components/ui/tableTemplate/cellRenderers';
import { ContractStatusBadge } from '../components/ContractStatusBadge';
import { resolveContractStatus } from '../utils/contractStatus';
import { resolveLabeledCode } from '../../../lib/translateCode';
import type { LabeledCode } from '../../../lib/labeledCode';
import { COLUMN_WIDTH } from '../../../shared/constants';
import type { EmployeeTableColumn } from '../employeeTypes';
import type { EmployeeFilterOptions } from '../api/employeeApi';
import type { ListFilterDef } from '../../../components/ui/tableTemplate/TableTemplateTypes';
import {
  EMPLOYEE_FIELD_CODES,
  EMPLOYEE_GENDERS,
  EMPLOYEE_MARITAL_STATUSES,
  EMPLOYEE_COUNTRIES,
  EMPLOYEE_CONTRACT_CREATION_TYPES,
  EMPLOYEE_CONTRACT_TYPES,
  EMPLOYEE_CONTRACT_SALARY_SCALES,
  EMPLOYEE_CONTRACT_LEVELS,
  EMPLOYEE_JOB_FUNCTIONS,
} from '../config/employeeFilterOptions';

const I18N_PREFIX = 'employeeTables.columns';
const FILTER_OPTIONS_PREFIX = 'employeeTables.filterOptions';
// Reuse the exact status labels shown by the list badge (ContractStatusBadge),
// so the `contract_current` filter reads identically to the column values.
const CONTRACT_STATUS_PREFIX = 'employeeTables.contractStatus';

// `none` = no current contract (`contratencours !== 'O'`, including terminated /
// empty / null), matching resolveContractStatus.
const CONTRATENCOURS_NOT_CURRENT = ['N', 'empty', 'null'];

/**
 * Serializes a `contract_current` status selection (activation-on options
 * `currentActivated` / `currentNotActivated` / `none`) into the underlying
 * Homère columns: `contratencours` (current?) + `statutactive` (activated?).
 *
 * The backend ANDs distinct columns, so a selection mixing `none` with a current
 * state ("non en cours OR activé") cannot be expressed as one AND of per-column
 * sets. In that case we keep the broader `contratencours` filter and drop the
 * `statutactive` constraint (a safe over-fetch) rather than emit a query that
 * returns nothing. Single-select — the intended use — is always exact.
 */
function serializeContractStatusFilter(qs: URLSearchParams, selectedValues: string[]): void {
  const contratencours = new Set<string>();
  const statutactive = new Set<string>();
  const hasNone = selectedValues.includes('none');
  const wantsActivated = selectedValues.includes('currentActivated');
  const wantsNotActivated = selectedValues.includes('currentNotActivated');

  if (wantsActivated || wantsNotActivated) {
    contratencours.add('O');
    if (wantsActivated) statutactive.add('1');
    // Not activated = statutactive 0 OR null (both render as "Non activé").
    if (wantsNotActivated) {
      statutactive.add('0');
      statutactive.add('null');
    }
  }
  if (hasNone) CONTRATENCOURS_NOT_CURRENT.forEach((v) => contratencours.add(v));

  if (contratencours.size > 0) qs.set('contratencours[in]', [...contratencours].join(','));
  if (statutactive.size > 0 && !hasNone) qs.set('statutactive[in]', [...statutactive].join(','));
}

export const useDefaultEmployeeColumns = (
  filterOptions?: EmployeeFilterOptions,
): EmployeeTableColumn[] => {
  const { t } = useTranslation('common');

  return useMemo(() => {
    // Renders a DICO field (LabeledCode or plain string) as its display label.
    // Priority: backend libelle → i18n translation → raw code.
    const codeCell = (
      suffix: string,
      value: LabeledCode | string | null | undefined,
      sx?: object,
    ) =>
      renderCell('text', resolveLabeledCode(t, `${FILTER_OPTIONS_PREFIX}.${suffix}`, value), { t }, sx);

    // O/N flag cell (délocalisé, période d'essai, délégué, pool urgence): shows
    // the Oui/Non label, but an unset flag stays blank — including the backend's
    // `{ code: '', libelle: null }` shape (empty-string code), which otherwise
    // resolves to the `yesNo.emptyOrNull` label "Non renseigné" (HOMCO-849 fix).
    const flagCell = (value: LabeledCode | string | null | undefined) => {
      const code = typeof value === 'object' && value !== null ? value.code : value;
      return codeCell('yesNo', code ? value : null);
    };

    // Installation option (per ONG); off until the backend `global` table ships.
    const contractActivationEnabled = filterOptions?.contractActivationEnabled ?? false;

    // Current-contract status filter — options carry the same `contractStatus`
    // labels as the list badge. Typed as ListFilterDef so its (option-specific)
    // valueAliases / serialize don't widen the columns array's inferred type.
    const contractCurrentFilter: ListFilterDef = contractActivationEnabled
      ? {
          kind: 'list',
          optionLabelPrefix: CONTRACT_STATUS_PREFIX,
          options: ['currentActivated', 'currentNotActivated', 'none'],
          serialize: serializeContractStatusFilter,
        }
      : {
          kind: 'list',
          optionLabelPrefix: CONTRACT_STATUS_PREFIX,
          options: ['current', 'none'],
          // Activation off: status is purely `contratencours` — 'none' = not
          // current (N / '' / NULL), matching resolveContractStatus.
          valueAliases: { current: ['O'], none: CONTRATENCOURS_NOT_CURRENT },
        };

    // Default column order (the 9 `defaultVisible` columns, in display order):
    //   Field code · Current contract · File number · Last name · First name ·
    //   Function · Initial start date · Contract end date · Birthdate.
    // The first five carry `sticky: true` and MUST stay contiguous and first in
    // this array — the table pins them left by cumulating their widths in display
    // order (see TableTemplate `stickyMeta`). Non-default columns follow, grouped,
    // and surface in the column picker via their `group` field (order within a
    // group = their order here).
    return [
      // ── Frozen leading block (sticky-left) ────────────────────────────────────
      {
        key: 'field_code',
        databaseName: 'codeterrain',
        labelKey: `${I18N_PREFIX}.fieldCode`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.fieldCode`,
          options: filterOptions?.codeterrain ?? EMPLOYEE_FIELD_CODES,
        },
        group: 'location',
        defaultVisible: true,
        sticky: true,
        // Terrain label comes from the DB (`v_employee_list` joins
        // `terrain.libelle`; the backend sends `{code, libelle}` and
        // resolveLabeledCode prefers the libelle) — never from a
        // hand-maintained i18n copy (spec employee-terrain-labels,
        // HOMCO-1079). Raw code as fallback when the libelle is NULL.
        render: row => codeCell('fieldCode', row.field_code),
      },
      {
        key: 'contract_current',
        databaseName: 'contratencours',
        labelKey: `${I18N_PREFIX}.contractCurrent`,
        type: 'text',
        width: COLUMN_WIDTH.multiValue,
        sortable: true,
        // Filter options mirror the list status badge (same `contractStatus`
        // labels); see `contractCurrentFilter` for the column mapping.
        filter: contractCurrentFilter,
        group: 'contractInformation',
        defaultVisible: true,
        sticky: true,
        render: row => (
          <ContractStatusBadge
            status={resolveContractStatus({
              activationOptionEnabled: contractActivationEnabled,
              hasCurrentContract: row.contract_current?.code === 'O',
              isActivated: row.is_active === true,
            })}
          />
        ),
      },
      {
        key: 'file_number',
        databaseName: 'code_staff',
        labelKey: `${I18N_PREFIX}.fileNumber`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'identity',
        defaultVisible: true,
        sticky: true,
        render: row => renderCell('text', row.file_number, { t }),
      },
      {
        key: 'last_name',
        databaseName: 'nom',
        labelKey: `${I18N_PREFIX}.lastName`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'identity',
        defaultVisible: true,
        sticky: true,
        render: row => renderCell('text', row.last_name, { t }, boldCellSx),
      },
      {
        key: 'first_name',
        databaseName: 'prenom',
        labelKey: `${I18N_PREFIX}.firstName`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'identity',
        defaultVisible: true,
        sticky: true,
        render: row => renderCell('text', row.first_name, { t }, boldCellSx),
      },

      // ── Remaining default columns (scroll) ────────────────────────────────────
      {
        key: 'job_function',
        databaseName: 'fonction',
        labelKey: `${I18N_PREFIX}.jobFunction`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.jobFunction`,
          options: filterOptions?.fonction ?? EMPLOYEE_JOB_FUNCTIONS,
        },
        group: 'positionRemuneration',
        defaultVisible: true,
        render: row => codeCell('jobFunction', row.job_function),
      },
      {
        key: 'initial_start_date',
        databaseName: 'initialstartdate',
        labelKey: `${I18N_PREFIX}.initialStartDate`,
        type: 'date',
        width: COLUMN_WIDTH.date,
        sortable: true,
        align: 'center',
        filter: { kind: 'date' },
        group: 'employmentDates',
        defaultVisible: true,
        render: row => renderCell('date', row.initial_start_date, { t }),
      },
      {
        key: 'contract_end_date',
        databaseName: 'datefin',
        labelKey: `${I18N_PREFIX}.contractEndDate`,
        type: 'date',
        width: COLUMN_WIDTH.date,
        sortable: true,
        align: 'center',
        filter: { kind: 'date' },
        group: 'employmentDates',
        defaultVisible: true,
        render: row => renderCell('date', row.contract_end_date, { t }),
      },
      {
        key: 'birthdate',
        databaseName: 'datenaissance',
        labelKey: `${I18N_PREFIX}.birthdate`,
        type: 'date',
        width: COLUMN_WIDTH.date,
        sortable: true,
        align: 'center',
        filter: { kind: 'date' },
        group: 'identity',
        defaultVisible: true,
        render: row => renderCell('date', row.birthdate, { t }),
      },

      // ── Identity (extra, off by default) ──────────────────────────────────────
      {
        key: 'gender',
        databaseName: 'sexe',
        labelKey: `${I18N_PREFIX}.gender`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.gender`,
          options: filterOptions?.sexe ?? EMPLOYEE_GENDERS,
        },
        group: 'identity',
        defaultVisible: false,
        render: row => codeCell('gender', row.gender),
      },
      {
        key: 'marital_status',
        databaseName: 'statutfamilial',
        labelKey: `${I18N_PREFIX}.maritalStatus`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.maritalStatus`,
          options: filterOptions?.statutfamilial ?? EMPLOYEE_MARITAL_STATUSES,
        },
        group: 'identity',
        defaultVisible: false,
        render: row => codeCell('maritalStatus', row.marital_status),
      },

      // ── Location (extra, off by default) ──────────────────────────────────────
      {
        key: 'country',
        databaseName: 'pays',
        labelKey: `${I18N_PREFIX}.country`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.country`,
          options: filterOptions?.pays ?? EMPLOYEE_COUNTRIES,
        },
        group: 'location',
        defaultVisible: false,
        render: row => codeCell('country', row.country),
      },
      {
        key: 'relocated_staff',
        databaseName: 'relocatedstaff',
        labelKey: `${I18N_PREFIX}.relocatedStaff`,
        type: 'text',
        width: COLUMN_WIDTH.boolean,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.yesNo`,
          options: filterOptions?.relocatedstaff
            ? [...filterOptions.relocatedstaff, 'emptyOrNull']
            : ['O', 'N', 'emptyOrNull'],
          valueAliases: { emptyOrNull: ['null', 'empty'] },
        },
        group: 'location',
        defaultVisible: false,
        align: 'center',
        render: row => flagCell(row.relocated_staff),
      },

      // ── Employment dates (extra, off by default) ──────────────────────────────
      {
        key: 'entry_date',
        databaseName: 'dateentree',
        labelKey: `${I18N_PREFIX}.entryDate`,
        type: 'date',
        width: COLUMN_WIDTH.date,
        sortable: true,
        align: 'center',
        filter: { kind: 'date' },
        group: 'employmentDates',
        defaultVisible: false,
        render: row => renderCell('date', row.entry_date, { t }),
      },
      {
        key: 'contract_start_date',
        databaseName: 'datedeb',
        labelKey: `${I18N_PREFIX}.contractStartDate`,
        type: 'date',
        width: COLUMN_WIDTH.date,
        sortable: true,
        align: 'center',
        filter: { kind: 'date' },
        group: 'employmentDates',
        defaultVisible: false,
        render: row => renderCell('date', row.contract_start_date, { t }),
      },
      {
        key: 'update_date',
        databaseName: 'hc_last_update',
        labelKey: `${I18N_PREFIX}.updateDate`,
        type: 'date',
        width: COLUMN_WIDTH.date,
        sortable: true,
        align: 'center',
        filter: { kind: 'date' },
        group: 'employmentDates',
        defaultVisible: false,
        render: row => renderCell('date', row.update_date, { t }),
      },

      // ── Contract information (extra, off by default) ──────────────────────────
      {
        key: 'contract_creation_type',
        databaseName: 'typecreation',
        labelKey: `${I18N_PREFIX}.contractCreationType`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.contractCreationType`,
          options: filterOptions?.typecreation ?? EMPLOYEE_CONTRACT_CREATION_TYPES,
        },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('contractCreationType', row.contract_creation_type),
      },
      {
        key: 'contract_type',
        databaseName: 'typecontrat',
        labelKey: `${I18N_PREFIX}.contractType`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.contractType`,
          options: filterOptions?.typecontrat ?? EMPLOYEE_CONTRACT_TYPES,
        },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('contractType', row.contract_type),
      },
      {
        key: 'contract_activity_rate',
        databaseName: 'tauxactivite',
        labelKey: `${I18N_PREFIX}.contractActivityRate`,
        type: 'number',
        width: COLUMN_WIDTH.number,
        sortable: true,
        // Taux d'activité borné 0–100 (validation + garde-fou natif du champ).
        filter: { kind: 'numeric', inputProps: { min: 0, max: 100 } },
        group: 'contractInformation',
        defaultVisible: false,
        render: row =>
          renderCell(
            'text',
            row.contract_activity_rate != null ? String(row.contract_activity_rate) : null,
            { t },
          ),
      },
      {
        key: 'contract_trial_period',
        databaseName: 'periodeessai',
        labelKey: `${I18N_PREFIX}.contractTrialPeriod`,
        type: 'text',
        width: COLUMN_WIDTH.boolean,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.yesNo`,
          options: filterOptions?.periodeessai
            ? [...filterOptions.periodeessai, 'emptyOrNull']
            : ['O', 'N', 'emptyOrNull'],
          valueAliases: { emptyOrNull: ['null', 'empty'] },
        },
        group: 'contractInformation',
        defaultVisible: false,
        align: 'center',
        render: row => flagCell(row.contract_trial_period),
      },

      // ── Position & Remuneration (extra, off by default) ───────────────────────
      {
        key: 'contract_level',
        databaseName: 'niveau',
        labelKey: `${I18N_PREFIX}.contractLevel`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.contractLevel`,
          options: EMPLOYEE_CONTRACT_LEVELS,
        },
        group: 'positionRemuneration',
        defaultVisible: false,
        render: row => renderCell('text', row.contract_level, { t }),
      },
      {
        key: 'contract_salary_scale',
        databaseName: 'codegrillesalaire',
        labelKey: `${I18N_PREFIX}.contractSalaryScale`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.contractSalaryScale`,
          options: filterOptions?.codegrillesalaire ?? EMPLOYEE_CONTRACT_SALARY_SCALES,
        },
        group: 'positionRemuneration',
        defaultVisible: false,
        render: row => codeCell('contractSalaryScale', row.contract_salary_scale),
      },

      // ── HOMCO-849 — staff representation + current-contract allocations ────────
      // Backend-agnostic: `databaseName` = the list DTO field name (so display works
      // once the backend exposes it), null-safe render, text filter (no dependency
      // on the `filter-options` endpoint). All off by default.
      {
        key: 'delegue',
        databaseName: 'delegue',
        labelKey: `${I18N_PREFIX}.staffDelegate`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        // Binary field (O/N): Oui / Non / Non renseigné (HOMCO-849 fix).
        // Static options — no dependency on the filter-options endpoint.
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.yesNo`,
          options: ['O', 'N', 'emptyOrNull'],
          valueAliases: { emptyOrNull: ['null', 'empty'] },
        },
        group: 'identity',
        defaultVisible: false,
        align: 'center',
        render: row => flagCell(row.delegue),
      },
      {
        key: 'poolurgence',
        databaseName: 'poolurgence',
        labelKey: `${I18N_PREFIX}.emergencyPool`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        // Binary field (O/N): Oui / Non / Non renseigné (HOMCO-849 fix).
        // Static options — no dependency on the filter-options endpoint.
        filter: {
          kind: 'list',
          optionLabelPrefix: `${FILTER_OPTIONS_PREFIX}.yesNo`,
          options: ['O', 'N', 'emptyOrNull'],
          valueAliases: { emptyOrNull: ['null', 'empty'] },
        },
        group: 'identity',
        defaultVisible: false,
        align: 'center',
        render: row => flagCell(row.poolurgence),
      },
      {
        key: 'code_line_manager',
        databaseName: 'code_line_manager',
        labelKey: `${I18N_PREFIX}.lineManager`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('lineManager', row.code_line_manager),
      },
      {
        key: 'code_func_manager',
        databaseName: 'code_func_manager',
        labelKey: `${I18N_PREFIX}.funcManager`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('funcManager', row.code_func_manager),
      },
      {
        key: 'categorie',
        databaseName: 'categorie',
        labelKey: `${I18N_PREFIX}.jobCategory`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'positionRemuneration',
        defaultVisible: false,
        render: row => codeCell('jobCategory', row.categorie),
      },
      {
        key: 'codeanalytique',
        databaseName: 'codeanalytique',
        labelKey: `${I18N_PREFIX}.projectCode`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('projectCode', row.codeanalytique),
      },
      {
        key: 'axeanalytique',
        databaseName: 'axeanalytique',
        labelKey: `${I18N_PREFIX}.subProject`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('subProject', row.axeanalytique),
      },
      {
        key: 'axesaga',
        databaseName: 'axesaga',
        labelKey: `${I18N_PREFIX}.analyticalCode`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('analyticalCode', row.axesaga),
      },
      {
        key: 'ligneaxesaga',
        databaseName: 'ligneaxesaga',
        labelKey: `${I18N_PREFIX}.analyticalLine`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('analyticalLine', row.ligneaxesaga),
      },
      {
        key: 'codecomptable',
        databaseName: 'codecomptable',
        labelKey: `${I18N_PREFIX}.accountingCode`,
        type: 'text',
        width: COLUMN_WIDTH.text,
        sortable: true,
        filter: { kind: 'text' },
        group: 'contractInformation',
        defaultVisible: false,
        render: row => codeCell('accountingCode', row.codecomptable),
      },
    ];
  }, [t, filterOptions]);
};
